<template>
  <section class="bg-background-light py-20 lg:py-28" :id="id">
    <div class="mx-auto max-w-[1200px] px-6 lg:px-10">
      <div class="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 class="max-w-[600px] text-4xl font-black leading-[1.05] tracking-[-0.02em] text-text-main sm:text-5xl">
            {{ t('home_team.title') }}
          </h2>
          <p class="mt-4 max-w-[520px] text-lg text-text-muted">{{ t('home_team.subtitle') }}</p>
        </div>

        <RouterLink
          :to="getRoute('/team')"
          class="hidden shrink-0 items-center gap-2 rounded-xl border border-text-main/15 bg-white px-5 py-3 text-sm font-bold text-text-main transition-colors hover:border-text-main/40 sm:inline-flex"
        >
          {{ t('home_team.link') }}
          <span class="material-symbols-outlined text-[18px]">arrow_forward</span>
        </RouterLink>
      </div>

      <ul class="mt-12 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
        <li v-for="member in teamMembers" :key="member.name" class="group">
          <div class="overflow-hidden rounded-2xl border border-text-main/10 bg-white">
            <img
              :src="member.image"
              :alt="member.name"
              width="400"
              height="400"
              class="aspect-square w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
              loading="lazy"
              decoding="async"
            />
          </div>
          <p class="mt-3 font-bold text-text-main">{{ member.name }}</p>
          <p class="text-sm text-text-muted">{{ roleFor(member) }}</p>
        </li>
      </ul>

      <div class="mt-10 flex justify-center sm:hidden">
        <RouterLink
          :to="getRoute('/team')"
          class="inline-flex items-center gap-2 rounded-xl border border-text-main/15 bg-white px-6 py-3 text-sm font-bold text-text-main"
        >
          {{ t('home_team.link') }}
          <span class="material-symbols-outlined text-[18px]">arrow_forward</span>
        </RouterLink>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';

// Same source as the Team page, so a new hire only has to be added once.
import { teamMembers, type TeamMember } from '../../data/team';

defineProps<{ id?: string }>();

const { t, locale } = useI18n();

const activeLocale = computed<'nl' | 'en'>(() => (locale.value === 'nl' ? 'nl' : 'en'));

const roleFor = (member: TeamMember) => member[activeLocale.value]?.role ?? member.en.role;

const getRoute = (path: string) => {
  const currentLocale = locale.value;
  if (path.startsWith('/')) return `/${currentLocale}${path}`.replace('//', '/');
  return path;
};
</script>
